/**
 * §P.2 · Grupos, horarios y matrículas.
 */

export interface Grupo {
  readonly id: string;
  readonly venue_id: string;
  readonly venue_name: string | null;
  readonly discipline_id: string;
  readonly name: string;
  readonly level: string | null;
  readonly age_min: number | null;
  readonly age_max: number | null;
  /** La gente que cabe en el tatami. `null` es sin límite declarado. */
  readonly capacity: number | null;
  readonly color_hex: string | null;
  readonly active: boolean;
  readonly enrolled_count: number;
  /** Franjas del horario vigentes hoy. `0`: no genera clases ni se pasa lista. */
  readonly schedule_slots: number;
}

/**
 * Una franja del horario semanal.
 *
 * **`weekday` es ISO: 1 es lunes y 7 es domingo.** No es la convención de
 * JavaScript, donde `Date.getDay()` devuelve 0 para domingo. Ahí está el error
 * clásico de la semana desplazada, y por eso los días de la semana de esta
 * pantalla salen de `DIAS`, que va indexado por el valor ISO.
 *
 * Y las horas son **locales del club** en `HH:MM`. No se convierten a UTC aquí: lo
 * hace PostgreSQL al generar las sesiones, con la zona del club, y lo hace bien
 * incluso el fin de semana del cambio de hora — que es cuando convertir en
 * JavaScript desplazaría todas las clases.
 */
export interface Franja {
  readonly id?: string | undefined;
  readonly weekday: number;
  readonly starts_at: string;
  readonly ends_at: string;
}

export interface Matricula {
  readonly id: string;
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly joined_on: Date | string;
  /** Con fecha, el alumno estuvo y ya no está. **No se borra**: su asistencia de
   * octubre solo se explica si consta que en octubre era del grupo. */
  readonly left_on: Date | string | null;
}

export interface Sede {
  readonly id: string;
  readonly name: string;
  readonly active?: boolean | undefined;
}

export interface Disciplina {
  readonly id: string;
  readonly name: string;
}

/**
 * Las disciplinas para un grupo nuevo: **primero las del club** · 03-11-2026.
 *
 * El desplegable enseñaba el catálogo entero de Kiap sin elegir nada, y un club de
 * taekwondo tenía que buscar el suyo entre kárate, judo y los demás. Ahora salen
 * arriba las que el club imparte (`GET /clubs/{club}/disciplines`) y, si es una
 * sola, viene elegida. Las demás siguen ahí: un club puede abrir un grupo de una
 * disciplina nueva.
 */
export function disciplinasParaUnGrupo(
  todas: readonly Disciplina[],
  delClub: readonly { readonly discipline_id: string }[]
): {
  readonly suyas: readonly Disciplina[];
  readonly otras: readonly Disciplina[];
  /** La que viene elegida: la única del club, o `''`. */
  readonly unica: string;
} {
  const ids = new Set(delClub.map((d) => d.discipline_id));
  const suyas = todas.filter((d) => ids.has(d.id));
  return {
    suyas,
    otras: todas.filter((d) => !ids.has(d.id)),
    unica: suyas.length === 1 ? suyas[0]!.id : '',
  };
}

/** Indexado por el valor ISO: `DIAS[1]` es lunes. La posición 0 no se usa. */
export const DIAS: readonly string[] = [
  '',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
];

export const DIAS_ISO: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

export const MAXIMO_DE_FRANJAS = 21;

/** «18 de 20», que es lo que necesita quien atiende la llamada de una familia. */
export const ocupacion = (g: Grupo): string =>
  g.capacity === null
    ? `${g.enrolled_count}`
    : `${g.enrolled_count} de ${g.capacity}`;

export const estaCompleto = (g: Grupo): boolean =>
  g.capacity !== null && g.enrolled_count >= g.capacity;

export const rangoDeEdad = (g: Grupo): string | null => {
  if (g.age_min === null && g.age_max === null) return null;
  if (g.age_min !== null && g.age_max !== null)
    return `${g.age_min}–${g.age_max} años`;
  if (g.age_min !== null) return `desde ${g.age_min} años`;
  return `hasta ${g.age_max!} años`;
};

/**
 * ¿Se pisan dos franjas del mismo día?
 *
 * El servidor responde `422 overlapping_schedules`, y con razón: sería la misma
 * gente en dos sitios. Se comprueba también aquí para poder **marcar el día**
 * concreto en el formulario en vez de enseñar un aviso genérico.
 *
 * Devuelve los días ISO en conflicto.
 */
export function diasQueSePisan(franjas: readonly Franja[]): readonly number[] {
  const conflictivos = new Set<number>();

  for (const dia of DIAS_ISO) {
    const delDia = franjas
      .filter((f) => f.weekday === dia && f.starts_at && f.ends_at)
      .slice()
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at));

    for (let i = 1; i < delDia.length; i += 1) {
      // Tocarse no es pisarse: una clase que acaba a las 18:30 y otra que empieza
      // a las 18:30 son seguidas, que es lo normal en un tatami.
      if (delDia[i]!.starts_at < delDia[i - 1]!.ends_at) conflictivos.add(dia);
    }
  }

  return [...conflictivos].sort((a, b) => a - b);
}

/** Una franja vale si tiene las dos horas y acaba después de empezar. */
export const franjaValida = (f: Franja): boolean =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(f.starts_at) &&
  /^([01]\d|2[0-3]):[0-5]\d$/.test(f.ends_at) &&
  f.ends_at > f.starts_at;
